const configuredBase = import.meta.env.VITE_API_BASE_URL?.trim()
export const API_BASE_URL = (configuredBase || '/api/v1').replace(/\/$/, '')

const ACCESS_TOKEN_KEY = 'plotfy:access_token'
const REFRESH_TOKEN_KEY = 'plotfy:refresh_token'
const SESSION_FLAG_KEY = 'plotfy:has_session'

let inMemoryAccessToken = null
let inMemoryRefreshToken = null
let refreshRequest = null

export class ApiError extends Error {
  constructor(message, { status = 0, code = 'REQUEST_FAILED', details = null } = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }
}

export function getAccessToken() {
  if (inMemoryAccessToken) return inMemoryAccessToken
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      return sessionStorage.getItem(ACCESS_TOKEN_KEY)
    }
  } catch {}
  return null
}

export function getRefreshToken() {
  if (inMemoryRefreshToken) return inMemoryRefreshToken
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      return sessionStorage.getItem(REFRESH_TOKEN_KEY)
    }
  } catch {}
  return null
}

export function hasActiveSession() {
  if (inMemoryAccessToken || inMemoryRefreshToken) return true
  try {
    if (typeof window !== 'undefined') {
      if (window.sessionStorage) {
        if (sessionStorage.getItem(ACCESS_TOKEN_KEY) || sessionStorage.getItem(REFRESH_TOKEN_KEY)) {
          return true
        }
      }
      if (window.localStorage) {
        if (localStorage.getItem(SESSION_FLAG_KEY) === 'true') {
          return true
        }
      }
    }
  } catch {}
  return false
}

export function setAuthTokens({ accessToken = null, refreshToken = null } = {}) {
  inMemoryAccessToken = accessToken || null
  inMemoryRefreshToken = refreshToken || null
  try {
    if (typeof window !== 'undefined') {
      if (window.sessionStorage) {
        if (accessToken) {
          sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
        } else {
          sessionStorage.removeItem(ACCESS_TOKEN_KEY)
        }

        if (refreshToken) {
          sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
        } else {
          sessionStorage.removeItem(REFRESH_TOKEN_KEY)
        }
      }

      if (window.localStorage) {
        if (accessToken || refreshToken) {
          localStorage.setItem(SESSION_FLAG_KEY, 'true')
        } else {
          localStorage.removeItem(SESSION_FLAG_KEY)
        }
      }
    }
  } catch {}
}

export function clearAuthTokens() {
  setAuthTokens({ accessToken: null, refreshToken: null })
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.removeItem(SESSION_FLAG_KEY)
    }
  } catch {}
}

export function setAccessToken(token) {
  setAuthTokens({ accessToken: token, refreshToken: getRefreshToken() })
}

function buildHeaders(body, headers = {}) {
  const nextHeaders = new Headers(headers)
  nextHeaders.set('Accept', 'application/json')

  if (body && !(body instanceof FormData)) {
    nextHeaders.set('Content-Type', 'application/json')
  }

  const token = getAccessToken()
  if (token) {
    nextHeaders.set('Authorization', `Bearer ${token}`)
  }

  const refreshToken = getRefreshToken()
  if (refreshToken) {
    nextHeaders.set('X-Refresh-Token', refreshToken)
  }

  return nextHeaders
}

async function parseResponse(response) {
  if (response.status === 204) return null
  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) return null
  return response.json()
}

function errorFromResponse(response, payload) {
  const serverError = payload?.error
  const defaultMessage = response.status === 401
    ? 'Sesi kamu telah berakhir. Silakan masuk kembali.'
    : 'Permintaan belum dapat diproses. Coba beberapa saat lagi.'

  return new ApiError(serverError?.message || defaultMessage, {
    status: response.status,
    code: serverError?.code || `HTTP_${response.status}`,
    details: serverError?.details || null,
  })
}

async function refreshSession() {
  if (!refreshRequest) {
    const currentRefreshToken = getRefreshToken()
    refreshRequest = fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(currentRefreshToken ? { 'X-Refresh-Token': currentRefreshToken } : {}),
      },
      body: JSON.stringify(currentRefreshToken ? { refreshToken: currentRefreshToken } : {}),
    })
      .then(async (response) => {
        const payload = await parseResponse(response)
        if (!response.ok) {
          clearAuthTokens()
          throw errorFromResponse(response, payload)
        }
        const nextAccessToken = payload?.data?.accessToken ?? payload?.data?.access_token
        const nextRefreshToken = payload?.data?.refreshToken ?? payload?.data?.refresh_token ?? currentRefreshToken
        setAuthTokens({ accessToken: nextAccessToken, refreshToken: nextRefreshToken })
        return payload
      })
      .finally(() => {
        refreshRequest = null
      })
  }

  return refreshRequest
}

export async function apiRequest(path, options = {}) {
  const {
    method = 'GET',
    body,
    headers,
    signal,
    retryAuth = true,
  } = options

  let response
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      credentials: 'include',
      headers: buildHeaders(body, headers),
      body: body == null || body instanceof FormData ? body : JSON.stringify(body),
      signal,
    })
  } catch (error) {
    if (error.name === 'AbortError') throw error
    throw new ApiError('Tidak dapat terhubung ke server Plotfy.', {
      code: 'NETWORK_ERROR',
    })
  }

  if (response.status === 401 && retryAuth && !path.startsWith('/auth/')) {
    if (hasActiveSession()) {
      try {
        await refreshSession()
        return apiRequest(path, { ...options, retryAuth: false })
      } catch {
        clearAuthTokens()
        window.dispatchEvent(new CustomEvent('plotfy:unauthorized'))
      }
    } else {
      clearAuthTokens()
      window.dispatchEvent(new CustomEvent('plotfy:unauthorized'))
    }
  }

  const payload = await parseResponse(response)
  if (!response.ok) throw errorFromResponse(response, payload)
  return payload
}

export function toQueryString(params = {}) {
  const query = new URLSearchParams()
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== '' && value !== null && value !== undefined) {
      query.set(key, String(value))
    }
  })
  const value = query.toString()
  return value ? `?${value}` : ''
}

export function unwrap(payload, fallback = null) {
  return payload?.data ?? fallback
}
